import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Image from "@tiptap/extension-image"
import Placeholder from "@tiptap/extension-placeholder"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"

export default class extends Controller {
  static targets = ["editor", "input"]
  static values = { 
    content: String, 
    placeholder: String,
    uploadUrl: String 
  }

  connect() {
    try {
      this.editor = new Editor({
        element: this.editorTarget,
        extensions: [
          StarterKit.configure({
            dropcursor: false,  // Désactiver DropCursor dans StarterKit 
            gapcursor: false,   // Désactiver GapCursor dans StarterKit
            underline: false,   // Désactiver Underline dans StarterKit pour éviter les doublons
            link: false,        // Désactiver Link dans StarterKit pour utiliser notre config personnalisée
          }),
          Image.configure({
            HTMLAttributes: {
              class: 'max-w-full h-auto rounded-lg',
            },
          }),
          Underline,
          Link.configure({
            openOnClick: false,
            isAllowedUri: (url, ctx) => {
              // Autoriser les variables de template {{...}}
              if (/^\{\{[^}]+\}\}$/.test(url)) return true
              return ctx.defaultValidate(url)
            },
            HTMLAttributes: {
              class: 'text-blue-600 underline hover:text-blue-800',
              rel: 'noopener noreferrer',
              target: '_blank',
            },
          }),
          Placeholder.configure({
            placeholder: this.placeholderValue || 'Tapez votre message ici...',
          }),
        ],
        content: this.contentValue,
        onUpdate: ({ editor }) => {
          this.inputTarget.value = editor.getHTML()
        },
        editorProps: {
          attributes: {
            class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-4',
          },
          handleDrop: (view, event, slice, moved) => {
            if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
              let file = event.dataTransfer.files[0]
              if (file.type.startsWith('image/')) {
                this.uploadImage(file)
                return true
              }
            }
            return false
          },
          handlePaste: (view, event, slice) => {
            if (event.clipboardData && event.clipboardData.files && event.clipboardData.files[0]) {
              let file = event.clipboardData.files[0]
              if (file.type.startsWith('image/')) {
                this.uploadImage(file)
                return true
              }
            }
            return false
          }
        },
      })

      // Ajouter les boutons de formatage
      this.createToolbar()
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de l\'éditeur Tiptap:', error)
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
    }
  }

  createToolbar() {
    const toolbar = document.createElement('div')
    toolbar.className = 'border-b border-gray-200 p-2 flex flex-wrap gap-1 mb-4'
    
    const buttons = [
      { name: 'bold', icon: 'B', title: 'Gras', action: () => this.editor.chain().focus().toggleBold().run() },
      { name: 'italic', icon: 'I', title: 'Italique', action: () => this.editor.chain().focus().toggleItalic().run() },
      { name: 'underline', icon: 'U', title: 'Souligné', action: () => this.editor.chain().focus().toggleUnderline().run() },
      { name: 'separator', type: 'separator' },
      { name: 'h1', icon: 'H1', title: 'Titre 1', action: () => this.editor.chain().focus().toggleHeading({ level: 1 }).run() },
      { name: 'h2', icon: 'H2', title: 'Titre 2', action: () => this.editor.chain().focus().toggleHeading({ level: 2 }).run() },
      { name: 'separator', type: 'separator' },
      { name: 'bulletList', icon: '•', title: 'Liste à puces', action: () => this.editor.chain().focus().toggleBulletList().run() },
      { name: 'orderedList', icon: '1.', title: 'Liste numérotée', action: () => this.editor.chain().focus().toggleOrderedList().run() },
      { name: 'separator', type: 'separator' },
      { name: 'link', icon: '🔗', title: 'Insérer un lien', action: () => this.insertLinkDialog() },
      { name: 'unlink', icon: '✂️', title: 'Supprimer le lien', action: () => this.editor.chain().focus().unsetLink().run() },
      { name: 'separator', type: 'separator' },
      { name: 'image', icon: '🖼️', title: 'Insérer une image', action: () => this.insertImageDialog() },
    ]

    buttons.forEach(button => {
      if (button.type === 'separator') {
        const separator = document.createElement('div')
        separator.className = 'w-px bg-gray-300 mx-1'
        toolbar.appendChild(separator)
      } else {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sz-primary-500'
        btn.innerHTML = button.icon
        btn.title = button.title
        // mousedown + preventDefault empêche le vol de focus sur l'éditeur
        btn.addEventListener('mousedown', (e) => {
          e.preventDefault()
          button.action()
        })
        toolbar.appendChild(btn)
      }
    })

    this.editorTarget.parentNode.insertBefore(toolbar, this.editorTarget)
  }

  insertImageDialog() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
      const file = e.target.files[0]
      if (file) {
        this.uploadImage(file)
      }
    }
    input.click()
  }

  async uploadImage(file) {
    const formData = new FormData();
    
    // Si c'est une nouvelle template (pas d'URL spécifique), utiliser l'endpoint Shrine générique
    if (!this.uploadUrlValue || this.uploadUrlValue === '#') {
      formData.append('file', file);
      const uploadUrl = '/upload';
      
      try {
        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        
        // Shrine retourne un objet avec id, storage, metadata, etc.
        if (data.id && data.storage) {
          // Pour un fichier uploadé en cache, nous devons obtenir l'URL signée
          // via notre endpoint Rails
          try {
            // Utiliser l'ID complet tel qu'il est retourné par Shrine
            const urlResponse = await fetch(`/admin/cached_images/${encodeURIComponent(data.id)}`, {
              headers: {
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
              }
            });
            
            if (urlResponse.ok) {
              const urlData = await urlResponse.json();
              this.editor.chain().focus().setImage({ src: urlData.url }).run()
            } else {
              throw new Error('Impossible d\'obtenir l\'URL signée');
            }
          } catch (error) {
            console.error('Erreur lors de l\'obtention de l\'URL:', error);
            alert('Erreur lors de l\'obtention de l\'URL de l\'image')
          }
        } else {
          console.error('Erreur lors de l\'upload:', data.error || 'Pas d\'ID ou storage dans la réponse')
          alert('Erreur lors de l\'upload de l\'image: ' + (data.error || 'Erreur inconnue'))
        }
      } catch (error) {
        console.error('Erreur lors de l\'upload:', error);
        alert('Erreur lors de l\'upload de l\'image')
      }
      return;
    }
    
    // Pour les templates existantes, utiliser l'URL spécifique
    formData.append('image', file);

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.url) {
        this.editor.chain().focus().setImage({ src: data.url }).run()
      } else {
        console.error('Erreur lors de l\'upload:', data.error)
        alert('Erreur lors de l\'upload de l\'image: ' + (data.error || 'Erreur inconnue'))
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      alert('Erreur lors de l\'upload de l\'image')
    }
  }

  insertLinkDialog() {
    const previousUrl = this.editor.getAttributes('link').href
    const url = window.prompt('URL du lien (ex: https://... ou {{booking_url}}) :', previousUrl || '')

    if (url === null) return

    if (url.trim() === '') {
      this.editor.commands.unsetLink()
      return
    }

    this.editor.commands.setLink({ href: url.trim() })
  }

  // Méthodes pour insérer des variables
  insertVariable(event) {
    const variable = event.currentTarget.dataset.variable
    if (variable && this.editor) {
      this.editor.chain().focus().insertContent(`{{${variable}}}`).run()
    }
  }
}
